import { UnitTransferRequestStatusEnumType } from '__generated__/graphql';

/**
 * The backend models a unit transfer as 16 statuses, most of which describe an internal ops
 * step ("documents under review" vs "conditions issued"). An investor only needs to know which
 * of five phases the transfer is in, and whose move it is. This module is the single place that
 * translation happens — the stepper, the next-action card and any copy that says "your turn"
 * all read from here so they can never disagree with each other.
 */

export type UnitTransferStageKey = 'request' | 'acceptance' | 'review' | 'documents' | 'signing';

/** Who the transfer is currently waiting on. Resolved against the viewer for the actual copy. */
export type UnitTransferActor = 'TRANSFEROR' | 'TRANSFEREE' | 'ANGELLIST' | 'ALL_PARTIES' | 'NONE';

export type UnitTransferViewerRole = 'TRANSFEROR' | 'TRANSFEREE';

export const UNIT_TRANSFER_STAGES: { key: UnitTransferStageKey; label: string }[] = [
  { key: 'request', label: 'Request' },
  { key: 'acceptance', label: 'Acceptance' },
  { key: 'review', label: 'Review' },
  { key: 'documents', label: 'Documents' },
  { key: 'signing', label: 'Signing' },
];

export type UnitTransferStage = {
  /** null once the request is closed without completing — there is no stage to sit in. */
  key: UnitTransferStageKey | null;
  /** Index into UNIT_TRANSFER_STAGES, or -1 when closed. */
  index: number;
  /** Rejected / withdrawn / declined — the journey ended early. */
  isClosed: boolean;
  /** READY_FOR_DIS — phase 1 is finished. */
  isComplete: boolean;
  waitingOn: UnitTransferActor;
};

const STAGE_INDEX: Record<UnitTransferStageKey, number> = UNIT_TRANSFER_STAGES.reduce(
  (acc, stage, index) => ({ ...acc, [stage.key]: index }),
  {} as Record<UnitTransferStageKey, number>
);

const buildStage = (
  key: UnitTransferStageKey,
  waitingOn: UnitTransferActor,
  { isComplete = false }: { isComplete?: boolean } = {}
): UnitTransferStage => ({
  key,
  index: STAGE_INDEX[key],
  isClosed: false,
  isComplete,
  waitingOn,
});

const closedStage = (): UnitTransferStage => ({
  key: null,
  index: -1,
  isClosed: true,
  isComplete: false,
  waitingOn: 'NONE',
});

export function getUnitTransferStage(status: UnitTransferRequestStatusEnumType): UnitTransferStage {
  switch (status) {
    case UnitTransferRequestStatusEnumType.Draft:
      return buildStage('request', 'TRANSFEROR');
    case UnitTransferRequestStatusEnumType.PendingTransfereeAcknowledgement:
      return buildStage('acceptance', 'TRANSFEREE');
    case UnitTransferRequestStatusEnumType.Submitted:
    case UnitTransferRequestStatusEnumType.UnderReview:
    case UnitTransferRequestStatusEnumType.PendingEscrowException:
      return buildStage('review', 'ANGELLIST');
    case UnitTransferRequestStatusEnumType.ConditionsIssued:
    case UnitTransferRequestStatusEnumType.PendingDocuments:
    case UnitTransferRequestStatusEnumType.DocumentsIncomplete:
      return buildStage('documents', 'TRANSFEROR');
    case UnitTransferRequestStatusEnumType.DocumentsUnderReview:
    case UnitTransferRequestStatusEnumType.DocumentsVerified:
      return buildStage('documents', 'ANGELLIST');
    case UnitTransferRequestStatusEnumType.PendingSignatures:
      return buildStage('signing', 'ALL_PARTIES');
    case UnitTransferRequestStatusEnumType.Executed:
      return buildStage('signing', 'ANGELLIST');
    case UnitTransferRequestStatusEnumType.ReadyForDis:
      return buildStage('signing', 'NONE', { isComplete: true });
    case UnitTransferRequestStatusEnumType.Rejected:
    case UnitTransferRequestStatusEnumType.Withdrawn:
    case UnitTransferRequestStatusEnumType.TransfereeDeclined:
      return closedStage();
    default:
      return closedStage();
  }
}

export default getUnitTransferStage;
